import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { flightSharesKeys } from './flightShares.queries';
import type { FlightShareCreateRequest } from '../../types/api';

// STD-015: write actions for the flight-sharing domain (#1182 Stage 3).
// apiService is imported here (a *.mutations.ts file) per STD-016.
//
// Both mutations suppress the global error toast (`meta.skipGlobalErrorToast`)
// and let the calling dialog render the failure inline instead. That is not
// stylistic: `SHARE_NO_SOLD_DATE` is a refusal whose resolution is a control in
// the dialog itself (the From-date field), so a toast that vanishes after five
// seconds and points at nothing is exactly the wrong surface for it. See
// `shareErrors.ts` for which codes get a UI-owned message and why.

/**
 * Grant read/write on flights to another organization.
 *
 * Invalidates the whole `flight-shares` namespace on success rather than the
 * one list key: a bulk grant writes rows for many flights, so the per-flight
 * lists, the drone-filtered list and the unfiltered "Shared" view can all have
 * changed, and enumerating them here would go stale the moment a caller adds a
 * filter.
 */
export function useGrantFlightSharesMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: async (body: FlightShareCreateRequest) => apiService.grantFlightShares(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: flightSharesKeys.all });
    },
  });
}

/**
 * Revoke a share by stamping `revoked_at`.
 *
 * Same broad invalidation, for the same reason: a revoked share disappears from
 * every live listing at once, and reappears in any listing that asked for
 * `include_revoked`.
 */
export function useRevokeFlightShareMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: async (shareId: string) => apiService.revokeFlightShare(shareId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: flightSharesKeys.all });
    },
  });
}
